"use client";

import React, { useState, useEffect } from "react";
import DashboardHeaderCard from "@/components/common/DashboardHeaderCard";
import { Route, Clock, CheckCircle, AlertCircle } from "lucide-react";
import tripService from "@/services/tripService";

const TripManagementHeader = ({ trips }) => {
  const [stats, setStats] = useState({
    totalTrips: 0,
    activeTrips: 0,
    completedTrips: 0,
    pendingTrips: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTripStats = async () => {
      try {
        setLoading(true);
        const statsData = await tripService.getDashboardStatistics();
        setStats({
          totalTrips: statsData.total_trips || 0,
          activeTrips: statsData.active_trips || 0,
          completedTrips: statsData.completed_trips || 0,
          pendingTrips: statsData.pending_trips || 0
        });
      } catch (error) {
        console.error("Failed to fetch dashboard statistics:", error);
        try {
          const allTripsData = await tripService.getAllTrips();
          let allTrips = [];
          if (allTripsData && typeof allTripsData === 'object' && Array.isArray(allTripsData.data)) {
            allTrips = allTripsData.data;
          } else if (Array.isArray(allTripsData)) {
            allTrips = allTripsData;
          } else if (allTripsData && typeof allTripsData === 'object' && Array.isArray(allTripsData.trips)) {
            allTrips = allTripsData.trips;
          }
          const totalTrips = allTrips.length;
          const activeTrips = allTrips.filter(trip => trip.status === 'ACTIVE').length;
          const completedTrips = allTrips.filter(trip => trip.status === 'COMPLETED' || trip.status === 'AUTO_COMPLETE' || trip.status === 'AUTO_COMPLETED').length;
          const pendingTrips = allTrips.filter(trip => trip.status === 'PENDING').length;
          setStats({
            totalTrips,
            activeTrips,
            completedTrips,
            pendingTrips
          });
        } catch (fallbackError) {
          console.error("Fallback method also failed:", fallbackError);
          const totalTrips = trips.length;
          const activeTrips = trips.filter(trip => trip.status === 'ACTIVE').length;
          const completedTrips = trips.filter(trip => trip.status === 'COMPLETED' || trip.status === 'AUTO_COMPLETE' || trip.status === 'AUTO_COMPLETED').length;
          const pendingTrips = trips.filter(trip => trip.status === 'PENDING').length;
          setStats({
            totalTrips,
            activeTrips,
            completedTrips,
            pendingTrips
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTripStats();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full">
        <DashboardHeaderCard
          name="TOTAL TRIPS"
          count="0"
          icon={<Route size={18} color="hsl(var(--primary))" />}
          differenceText="All trips in system"
        />

        <DashboardHeaderCard
          name="ACTIVE TRIPS"
          count="0"
          icon={<Clock size={18} color="hsl(var(--primary))" />}
          differenceText="Currently in progress"
        />

        <DashboardHeaderCard
          name="COMPLETED TRIPS"
          count="0"
          icon={<CheckCircle size={18} color="hsl(var(--primary))" />}
          differenceText="Successfully finished"
        />

        <DashboardHeaderCard
          name="PENDING TRIPS"
          count="0"
          icon={<AlertCircle size={18} color="hsl(var(--primary))" />}
          differenceText="Awaiting start"
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full">
      <DashboardHeaderCard
        name="TOTAL TRIPS"
        count={stats.totalTrips.toString()}
        icon={<Route size={18} color="hsl(var(--primary))" />}
        differenceText="All trips in system"
      />

      <DashboardHeaderCard
        name="ACTIVE TRIPS"
        count={stats.activeTrips.toString()}
        icon={<Clock size={18} color="hsl(var(--primary))" />}
        differenceText="Currently in progress"
      />

      <DashboardHeaderCard
        name="COMPLETED TRIPS"
        count={stats.completedTrips.toString()}
        icon={<CheckCircle size={18} color="hsl(var(--primary))" />}
        differenceText="Successfully finished"
      />

      <DashboardHeaderCard
        name="PENDING TRIPS"
        count={stats.pendingTrips.toString()}
        icon={<AlertCircle size={18} color="hsl(var(--primary))" />}
        differenceText="Awaiting start"
      />
    </div>
  );
};

export default TripManagementHeader;